import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { XCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

export const CANCEL_REASONS = [
  { code: "not_mine", label: "hc.thisIsNotMyItem" },
  { code: "found_elsewhere", label: "hc.iFoundItElsewhere" },
  { code: "fees_too_high", label: "hc.theFeesAreTooHigh" },
  { code: "no_longer_needed", label: "hc.iNoLongerNeedMy" },
  { code: "other", label: "hc.other" },
];

export function CancelReasonDialog({ open, onOpenChange, onConfirm, loading = false,
  title, description }) {
  const { t } = useLang();
  title = title ?? t("hc.cancellationReason");
  description = description ?? t("hc.tellUsWhyYouCancel");
  const [code, setCode] = useState("");
  const [text, setText] = useState("");
  const otherInvalid = code === "other" && !text.trim();
  const canConfirm = !!code && !otherInvalid && !loading;

  function reset() { setCode(""); setText(""); }

  function handleConfirm() {
    if (!canConfirm) return;
    onConfirm(code, code === "other" ? text.trim() : "");
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="cancel-reason-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><XCircle size={18} className="text-red-500" /> {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="cancel-reason-options">
          {CANCEL_REASONS.map((r) => (
            <button key={r.code} type="button" onClick={() => setCode(r.code)}
              data-testid={`cancel-reason-${r.code}`}
              className={`w-full text-left px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                code === r.code ? "border-findo-indigo bg-findo-indigo/5 text-findo-indigo" : "border-border bg-white text-slate-700 hover:bg-slate-50"
              }`}>
              {t(r.label)}
            </button>
          ))}
          {code === "other" && (
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
              placeholder={t("hc.specifyYourReason2")} className="rounded-xl mt-1" data-testid="cancel-reason-other-text" />
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cancel-reason-abort">{t("hc.back")}</Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="cancel-reason-confirm">
            {loading ? <FindoSpinner size={16} /> : t("hc.confirmCancellation")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
